import { usePageMeta } from "../../lib/seo";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Send, Loader2, ArrowLeft, Lightbulb } from 'lucide-react';
import axios from 'axios';
import Header from './Header';
import { scrollToSection } from '../../lib/scrollToSection';
import Footer from './Footer';
import MobilePhoneButton from './MobilePhoneButton';
import CookieConsent from './CookieConsent';
import { navData } from '../../data/mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AITipsPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`tips-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  usePageMeta({
    title: "Cleaning Tips & Advice | NeatWave Cleaning Services",
    description: "Free cleaning tips and advice from NeatWave Cleaning Services. Ask any cleaning question and get practical, eco-friendly answers.",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Welcome message - only run once on mount
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "👋 Hello! I'm your professional cleaning expert. Ask me anything about cleaning - from tough stains to eco-friendly methods, I'm here to help!",
        id: 'welcome-tips'
      }]);
    }
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input, id: `user-${Date.now()}` };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/ai/chat/cleaning-tips`, {
        message: input,
        session_id: sessionId
      });

      const aiMessage = { role: 'assistant', content: response.data.response, id: `ai-${Date.now()}` };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Cleaning tips error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I'm having trouble right now. For immediate help, call NeatWave at 0749 286 5011.",
        id: `error-${Date.now()}`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "How do I remove red wine stains from carpet?",
    "What's the best way to clean a greasy oven?",
    "How can I make my bathroom sparkle?",
    "What are eco-friendly cleaning alternatives?",
  ];

  const handleSuggestion = (question) => {
    setInput(question);
  };

  return (
    <div className="min-h-screen bg-white font-inter">
      <Header scrollToQuote={() => { navigate('/#quote-section'); scrollToSection('quote-section'); }} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sky-50 to-white pt-24 pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Home</span>
          </button>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-600 rounded-2xl mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              AI Cleaning Expert
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Get instant professional cleaning tips and advice. Ask any question about cleaning, stains, products, or techniques.
            </p>
          </div>
        </div>
      </section>

      {/* Chat Interface */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-slate-900">Try asking:</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {suggestedQuestions.map((question) => (
                  <button
                    key={question}
                    onClick={() => handleSuggestion(question)}
                    className="text-left p-3 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors text-sm text-slate-700"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="bg-slate-50 rounded-2xl border-2 border-slate-200 p-6 mb-6 min-h-[400px] max-h-[600px] overflow-y-auto space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-5 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-sky-600 text-white rounded-br-sm'
                      : 'bg-white text-slate-800 shadow-md rounded-bl-sm border border-slate-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white shadow-md px-5 py-3 rounded-2xl rounded-bl-sm border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
                    <span className="text-sm text-slate-600">Getting expert advice...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="bg-white rounded-xl border-2 border-slate-200 p-4 shadow-lg">
            <div className="flex gap-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask me any cleaning question..."
                disabled={loading}
                className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-600 disabled:bg-slate-100"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white px-6 py-3 rounded-xl transition-colors disabled:cursor-not-allowed font-semibold"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Powered by AI • Professional cleaning advice
            </p>
          </div>

          {/* Call to Action */}
          <div className="mt-12 bg-gradient-to-r from-sky-600 to-sky-700 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-2">Need Professional Help?</h3>
            <p className="mb-6 text-sky-100">
              While our AI can give you great tips, sometimes you need the professionals. Let NeatWave handle the heavy lifting.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => { navigate('/#quote-section'); scrollToSection('quote-section'); }}
                className="bg-white text-sky-600 hover:bg-sky-50 px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Get Free Quote
              </button>
              <a
                href={`tel:${navData.phoneLink}`}
                className="bg-sky-800 hover:bg-sky-900 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Call 0749 286 5011
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobilePhoneButton phoneLink={navData.phoneLink} />
      <CookieConsent />
    </div>
  );
};

export default AITipsPage;
